// frontend/src/components/asset/MyTrafficOffences.js
/**
 * Driver-facing traffic offence actions on /my-assets.
 *
 * Two things can be pending against a driver, and they are deliberately kept
 * as separate cards because they are separate decisions:
 *
 *   1. Confirm — "were you driving OU1234A at 08:42 on 3 Mar?" This is a legal
 *      statement about a named person, so the driver answers it themselves;
 *      the custody records only produce the suggestion. Disputing requires a
 *      reason, which is what the custodian works from to find the real driver.
 *
 *   2. Pay — the notice has been reissued in the driver's name. They settle it
 *      with the authority directly (the company never pays a driver-liable
 *      fine) and upload the receipt to close the loop.
 *
 * Rendered above the tabs on My Assets rather than inside one: these carry
 * statutory deadlines, and a driver who never opens the Transactions tab must
 * still see them.
 *
 * Theme: Employee (blue), with amber/red reserved for deadline urgency.
 */

import React, { useState } from 'react';
import {
  ShieldAlert, CalendarClock, Car, CircleDollarSign, MapPin,
  CheckCircle2, XCircle, Upload,
} from 'lucide-react';
import Modal from '../UI/Modal';
import SecureFileLink from './SecureFileLink';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { assetInventoryAPI } from '../../services/api';
import { useMyOffences, useRespondToOffence, useUploadMyOffenceReceipt } from '../../hooks/useAssetInventoryData';

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(`${dateStr}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};

/** Deadline strip. Colour tracks urgency, never status. */
const DeadlineStrip = ({ label, date }) => {
  const days = daysUntil(date);
  if (days === null) return null;

  const tone = days < 0
    ? 'bg-red-50 text-red-800 border-red-200'
    : days <= 3
      ? 'bg-amber-50 text-amber-800 border-amber-200'
      : 'bg-gray-50 text-gray-700 border-gray-200';
  const text = days < 0
    ? `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`
    : days === 0
      ? 'Due today'
      : `${days} day${days === 1 ? '' : 's'} left`;

  return (
    <div className={`rounded-lg border px-2.5 py-1.5 flex items-center gap-1.5 ${tone}`}>
      <CalendarClock className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="text-xs font-medium">{label}: {text}</span>
      <span className="text-[11px] opacity-75 ml-auto whitespace-nowrap">{formatDate(date)}</span>
    </div>
  );
};

const Fact = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1 text-xs text-gray-600">
    <Icon className="w-3 h-3 text-gray-400 flex-shrink-0" />
    {children}
  </span>
);

/** Shared header for both card types: what the notice is actually about. */
const NoticeFacts = ({ row }) => (
  <>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{row.offence_type}</p>
        <p className="text-xs text-gray-400 truncate">
          {row.issuing_authority} · {row.notice_number}
        </p>
      </div>
      <ShieldAlert className="w-5 h-5 text-red-500 flex-shrink-0" />
    </div>

    <div className="flex flex-wrap gap-x-3 gap-y-1">
      <Fact icon={Car}>{row.vehicle_number}</Fact>
      <Fact icon={CalendarClock}>{formatDateTime(row.offence_datetime)}</Fact>
      {row.offence_location && <Fact icon={MapPin}>{row.offence_location}</Fact>}
      <Fact icon={CircleDollarSign}>${Number(row.fine_amount || 0).toFixed(2)}</Fact>
    </div>
  </>
);

/** "Were you driving?" — the driver answers for themselves. */
const ConfirmCard = ({ row }) => {
  const [disputing, setDisputing] = useState(false);
  const [reason, setReason] = useState('');
  const respond = useRespondToOffence();

  const submit = (confirmed) => {
    respond.mutate({
      declaration_id: row.declaration_id,
      confirmed,
      ...(confirmed ? {} : { dispute_reason: reason.trim() }),
    });
  };

  return (
    <div className="bg-white rounded-xl border border-red-200 p-4 space-y-3">
      <NoticeFacts row={row} />

      <DeadlineStrip
        label={row.declaration_deadline ? 'Reply by' : 'Deadline'}
        date={row.declaration_deadline || row.payment_deadline}
      />

      {row.suggestion_basis && (
        <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-2.5 py-2">
          {row.suggestion_basis}
        </p>
      )}

      {row.has_notice_document !== false && (
        <SecureFileLink
          url={assetInventoryAPI.myOffenceDocumentUrl(row.notice_id)}
          label="View Notice"
          theme="employee"
          className="w-full sm:w-auto"
        />
      )}

      {disputing ? (
        <div className="space-y-2 rounded-lg bg-gray-50 p-3">
          <label htmlFor={`dispute-${row.declaration_id}`} className="block text-sm font-medium text-gray-700">
            Why weren&apos;t you driving?
          </label>
          <textarea
            id={`dispute-${row.declaration_id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. I handed the keys to Ravi on 2 Mar — he had it that whole week."
            className="input w-full"
          />
          <p className="text-xs text-gray-500">
            Name whoever you think was driving if you know. The fleet custodian works
            from this to trace the right person before the deadline.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              disabled={!reason.trim() || respond.isPending}
              onClick={() => submit(false)}
              className="btn btn-primary"
            >
              Submit Reply
            </button>
            <button
              type="button"
              onClick={() => { setDisputing(false); setReason(''); }}
              className="btn btn-outline"
            >
              Back
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm font-medium text-gray-900">Were you driving this vehicle?</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              disabled={respond.isPending}
              onClick={() => submit(true)}
              className="btn btn-primary"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Yes, that was me
            </button>
            <button
              type="button"
              disabled={respond.isPending}
              onClick={() => setDisputing(true)}
              className="btn btn-outline"
            >
              <XCircle className="w-4 h-4 mr-2" />
              No, it wasn&apos;t me
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Confirming means the notice will be reissued in your name — you pay the fine
            directly to the issuing authority.
          </p>
        </>
      )}
    </div>
  );
};

/** Confirmed and reissued: settle it, then attach the receipt. */
const PayCard = ({ row, onUpload }) => (
  <div className="bg-white rounded-xl border border-amber-200 p-4 space-y-3">
    <NoticeFacts row={row} />

    <DeadlineStrip label="Pay by" date={row.payment_deadline} />

    <div className="flex flex-col sm:flex-row gap-2">
      {row.has_notice_document !== false && (
        <SecureFileLink
          url={assetInventoryAPI.myOffenceDocumentUrl(row.notice_id)}
          label="View Notice"
          theme="employee"
        />
      )}
      <button type="button" onClick={() => onUpload(row)} className="btn btn-primary">
        <Upload className="w-4 h-4 mr-2" />
        {row.has_payment_proof ? 'Replace Receipt' : 'Upload Receipt'}
      </button>
    </div>

    <p className="text-xs text-gray-500">
      {row.has_payment_proof
        ? 'Receipt uploaded — the fleet custodian will verify it and close this off.'
        : 'Pay the authority directly, then upload the receipt here as proof.'}
    </p>
  </div>
);

const UploadReceiptModal = ({ isOpen, onClose, row }) => {
  const [file, setFile] = useState(null);
  const [reference, setReference] = useState('');
  const upload = useUploadMyOffenceReceipt();

  const close = () => {
    setFile(null);
    setReference('');
    onClose();
  };

  const submit = (e) => {
    e.preventDefault();
    if (!file) return;
    upload.mutate(
      { noticeId: row.notice_id, file, paymentReference: reference.trim() },
      { onSuccess: close },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Upload Payment Receipt"
      size="sm"
      theme="employee"
      footer={
        <>
          <button type="button" onClick={close} className="btn btn-outline">
            Cancel
          </button>
          <button
            type="submit"
            form="offence-receipt-form"
            disabled={!file || upload.isPending}
            className="btn btn-primary"
          >
            <Upload className="w-4 h-4 mr-2" />
            {upload.isPending ? 'Uploading...' : 'Upload'}
          </button>
        </>
      }
    >
      <form id="offence-receipt-form" onSubmit={submit} className="space-y-4">
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-sm font-medium text-blue-900">{row?.notice_number}</p>
          <p className="text-xs text-blue-700">
            {row?.vehicle_number} · ${Number(row?.fine_amount || 0).toFixed(2)}
          </p>
        </div>

        <div>
          <label htmlFor="offence-receipt-file" className="block text-sm font-medium text-gray-700 mb-1">
            Receipt <span className="text-red-500">*</span>
          </label>
          <input
            id="offence-receipt-file"
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="input w-full"
          />
          <p className="mt-1 text-xs text-gray-500">
            A screenshot of the payment confirmation or a PDF receipt is fine.
          </p>
        </div>

        <div>
          <label htmlFor="offence-receipt-ref" className="block text-sm font-medium text-gray-700 mb-1">
            Payment reference (optional)
          </label>
          <input
            id="offence-receipt-ref"
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Transaction / receipt number"
            className="input w-full"
          />
        </div>
      </form>
    </Modal>
  );
};

const MyTrafficOffences = () => {
  const { data: rows = [] } = useMyOffences();
  const [uploadRow, setUploadRow] = useState(null);

  if (rows.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
          <ShieldAlert className="w-4 h-4 text-red-600" />
        </div>
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-gray-900">
            Traffic Offences ({rows.length})
          </h2>
          <p className="text-xs text-gray-500">
            These have legal deadlines — please deal with them first.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rows.map((row) => (
          row.action === 'confirm'
            ? <ConfirmCard key={row.declaration_id} row={row} />
            : <PayCard key={row.declaration_id} row={row} onUpload={setUploadRow} />
        ))}
      </div>

      <UploadReceiptModal
        isOpen={!!uploadRow}
        onClose={() => setUploadRow(null)}
        row={uploadRow}
      />
    </div>
  );
};

export default MyTrafficOffences;
